
import { useState, useEffect } from "react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination";
import { Button } from "@/components/ui/button";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface DataPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  className?: string;
}

export function DataPagination({
  currentPage,
  totalPages,
  onPageChange,
  className,
}: DataPaginationProps) {
  const isMobile = useIsMobile();
  const [pagesToShow, setPagesToShow] = useState<Array<number | null>>([]);

  useEffect(() => {
    // Calculate which page numbers to show
    const calculatePagesToShow = () => {
      // Always show at least the current page
      if (totalPages <= 0) return [1]; 
      
      const pages: Array<number | null> = [];
      
      // On mobile, show fewer page numbers
      const maxVisiblePages = isMobile ? 3 : 5;
      
      if (totalPages <= maxVisiblePages) {
        // If we have fewer pages than max visible, show all pages
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Always show first page
        pages.push(1);
        
        // Calculate middle pages
        let startPage = Math.max(2, currentPage - Math.floor(maxVisiblePages / 2) + 1);
        let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 3);
        
        // Adjust if we're near the end
        if (endPage >= totalPages - 1) {
          endPage = totalPages - 1;
          startPage = Math.max(2, endPage - (maxVisiblePages - 3));
        }
        
        // Add ellipsis if needed
        if (startPage > 2) {
          pages.push(null); // ellipsis
        }
        
        // Add middle pages
        for (let i = startPage; i <= endPage; i++) {
          pages.push(i);
        }
        
        // Add ellipsis if needed
        if (endPage < totalPages - 1) {
          pages.push(null); // ellipsis
        }
        
        // Always show last page
        pages.push(totalPages);
      }
      
      return pages;
    };
    
    setPagesToShow(calculatePagesToShow());
  }, [currentPage, totalPages, isMobile]);

  // Always render pagination, even with just one page
  return (
    <Pagination className={className}>
      <PaginationContent>
        {currentPage > 1 ? (
          <>
            <PaginationItem>
              <Button 
                variant="ghost" 
                size="icon" 
                className="hidden md:flex h-8 w-8" 
                onClick={() => onPageChange(1)}
                disabled={currentPage === 1}
              >
                <ChevronsLeft className="h-4 w-4" />
                <span className="sr-only">First page</span>
              </Button>
            </PaginationItem>
            <PaginationItem>
              <PaginationPrevious 
                onClick={() => onPageChange(currentPage - 1)} 
                className="cursor-pointer"
              />
            </PaginationItem>
          </>
        ) : (
          <>
            <PaginationItem>
              <Button 
                variant="ghost" 
                size="icon" 
                className="hidden md:flex h-8 w-8" 
                onClick={() => onPageChange(1)}
                disabled={true}
              >
                <ChevronsLeft className="h-4 w-4" />
                <span className="sr-only">First page</span>
              </Button>
            </PaginationItem>
            <PaginationItem>
              <PaginationPrevious 
                className="pointer-events-none opacity-50"
              />
            </PaginationItem>
          </>
        )}

        {pagesToShow.map((page, i) => (
          page === null ? (
            <PaginationItem key={`ellipsis-${i}`}>
              <PaginationEllipsis />
            </PaginationItem>
          ) : (
            <PaginationItem key={page}>
              <PaginationLink 
                isActive={page === currentPage} 
                onClick={() => onPageChange(page)}
                className="cursor-pointer"
              >
                {page}
              </PaginationLink>
            </PaginationItem>
          )
        ))}

        {currentPage < totalPages ? (
          <>
            <PaginationItem>
              <PaginationNext 
                onClick={() => onPageChange(currentPage + 1)} 
                className="cursor-pointer"
              />
            </PaginationItem>
            <PaginationItem>
              <Button 
                variant="ghost" 
                size="icon" 
                className="hidden md:flex h-8 w-8"
                onClick={() => onPageChange(totalPages)}
                disabled={currentPage === totalPages}
              >
                <ChevronsRight className="h-4 w-4" />
                <span className="sr-only">Last page</span>
              </Button>
            </PaginationItem>
          </>
        ) : (
          <>
            <PaginationItem>
              <PaginationNext 
                className="pointer-events-none opacity-50"
              />
            </PaginationItem>
            <PaginationItem>
              <Button 
                variant="ghost" 
                size="icon" 
                className="hidden md:flex h-8 w-8"
                onClick={() => onPageChange(totalPages)}
                disabled={true}
              >
                <ChevronsRight className="h-4 w-4" />
                <span className="sr-only">Last page</span>
              </Button>
            </PaginationItem>
          </>
        )}
      </PaginationContent>
    </Pagination>
  );
}
